const { QUOTE_FIELDS, DISCOUNT_FIELDS } = require('../db/models');
const { PREMIUM_FIELDS, PAYMENT_PLANS, PAYMENT_PLAN_FACTORS, DISCOUNT_TYPES } = require('./enums');

/**
 *
 * @param {string|number} keyInput
 * @returns {string} a string stripped of non-digits
 */
function stripNonDigits(keyInput) {
  if (!keyInput && keyInput !== 0) return '';
  return keyInput.toString().replace(/\D/g, '');
}

/**
 * Formats a number as a whole number with commas, US
 * Note: does not handle negatives.
 *
 * @param {string|number} numString
 * @returns {string} A number string with commas, e.g. '1,000', '52,554'
 */
function formatInt(numString) {
  if (!numString && numString !== 0) return '';
  const cleanedString = stripNonFloatChars(numString);
  const parsedInt = parseInt(cleanedString);
  if (isNaN(parsedInt)) return '';
  return parsedInt.toLocaleString();
}

/**
 * Remove everything except numbers and periods.
 *
 * @param {string|number} keyInput Raw user input.
 * @returns {string} keyInput with any non-decimals, non-digits removed.
 */
function stripNonFloatChars(keyInput) {
  if (!keyInput && keyInput !== 0) return '';
  return keyInput.toString().replace(/[^0-9.]/g, '') || '';
}

/**
 * Format a float.
 *
 * @param {string|number} keyInput Raw user input.
 * @param {number} precision The number of digits to allow after the decimal.
 * @returns {string} Formatted number, e.g. '10.12'
 */
function formatDecimal(keyInput, precision = 100) {
  if (!keyInput && keyInput !== 0) return '';
  if (keyInput.toString().includes('.')) {
    const split = keyInput.toString().split('.');
    const integer = formatInt(split[0]);
    const decimals = stripNonDigits(split[1].substring(0, precision));
    return `${integer}.${decimals}`;
  } else {
    return formatInt(keyInput);
  }
}

/**
 * Prepends dollar sign to a string
 *
 * @param {string} numString
 * @returns {string} e.g '$1000'
 */
function addDollarSign(numString) {
  return numString && `$${numString}`;
}

/**
 * Format an amount as an integer with commas and a dollar sign
 *
 * @param {string|number} numString e.g. '1000.34'
 * @returns {string} e.g '$1,000'
 */
function formatMoney(numString) {
  if (!numString && numString !== 0) return '';
  const formatted = formatInt(numString);
  return addDollarSign(formatted);
}

/**
 * Format an amount as a USD amount, including cents
 *
 * @param {string|number} numString e.g. '1000.34'
 * @returns {string} e.g '$1,000.34'
 */
function formatMoneyWithDecimals(numString) {
  if (!numString && numString !== 0) return '';
  const CENTS_PRECISION = 2; // Allow only 2 chars after the decimal.
  const formatted = formatDecimal(numString, CENTS_PRECISION);
  return addDollarSign(formatted);
}

/**
 * Get the paid-in-full premium.
 *
 * @param {{}[]} premiums Array of premiums.
 * @returns {{}|undefined} The paid-in-full premium.
 */
function getPaidInFullPremium(premiums) {
  const annual = getCheapestPaymentPlan(premiums, PAYMENT_PLANS.ANNUALLY);
  if (annual) return annual;
  return getCheapestPaymentPlan(premiums, PAYMENT_PLANS.SEMI_ANNUALLY);
}

/**
 * Returns a single premium object with the matching payment plan and the lowest amount.
 *
 * @param {object[]} premiums Array of premiums.
 * @param {string} paymentPlan
 * @returns {object} The cheapest premium based on payment plan type
 */
function getCheapestPaymentPlan(premiums, paymentPlan) {
  if (paymentPlan === PAYMENT_PLANS.INSTALLMENT) return getCheapestInstallmentPlan(premiums);
  return premiums
    ?.filter(p => p[PREMIUM_FIELDS.PAYMENT_PLAN] === paymentPlan)
    ?.sort((a, b) => a[PREMIUM_FIELDS.AMOUNT] - b[PREMIUM_FIELDS.AMOUNT])[0];
}

/**
 * Return the cheapest installment plan
 *
 * @param {object[]} premiums
 * @returns {object}
 */
function getCheapestInstallmentPlan(premiums) {
  return premiums
    ?.filter(p => p[PREMIUM_FIELDS.PAYMENT_PLAN] === PAYMENT_PLANS.INSTALLMENT)
    ?.sort((a, b) => getTotalForInstallmentPlan(a) - getTotalForInstallmentPlan(b))[0];
}

/**
 * Return the sum of paid-in-full quote premiums.
 *
 * @param {Array<object>} quotes
 * @returns {number} Paid in full amount for the set of quotes
 */
function getBundledPrice(quotes) {
  const premiumAmounts = quotes.map(quote => {
    const premium = getPaidInFullPremium(quote?.premiums);
    return premium?.[PREMIUM_FIELDS.AMOUNT];
  });
  return premiumAmounts?.reduce((a, b) => a + b, 0);
}

/**
 * Return the total cost of an installment plan (exclusive of fees)
 *
 * @param {object} premium
 * @returns {number}
 */
function getTotalForInstallmentPlan(premium) {
  const DOWN_PAYMENT_COUNT = premium[PREMIUM_FIELDS.DOWN_PAYMENT] ? 1 : 0;
  return (
    premium[PREMIUM_FIELDS.DOWN_PAYMENT] +
    premium[PREMIUM_FIELDS.AMOUNT] * (premium[PREMIUM_FIELDS.NUM_OF_PAYMENTS] - DOWN_PAYMENT_COUNT)
  );
}

/**
 * Convert a given premium object into an estimated yearly amount
 * Note: Does not handle installment plans
 *
 * @param {object} premium A woop premium object
 * @returns {number} An estimated yearly premium amount
 */
function annualizePremium(premium) {
  const billingCycle = premium[PREMIUM_FIELDS.PAYMENT_PLAN];
  const factor = PAYMENT_PLAN_FACTORS[billingCycle];
  return factor * premium[PREMIUM_FIELDS.AMOUNT];
}

/**
 * Calculate the annual savings for a quote.
 *
 * @param {object} quote A woop quote object.
 * @param {object} policy A woop current policy object.
 * @param {number} minSavings A minimum dollar amount that constitutes savings
 * @returns {number}
 */
function calcSavings(quote, policy, minSavings = 10) {
  if (!quote || !policy) return 0;
  const policyPremium = getPaidInFullPremium(policy[QUOTE_FIELDS.PREMIUMS]);
  const quotePremium = getPaidInFullPremium(quote[QUOTE_FIELDS.PREMIUMS]);
  if (!(quotePremium && policyPremium)) return 0;

  const currentAnnualPremium = annualizePremium(policyPremium);
  const quoteAnnualPremium = annualizePremium(quotePremium);
  const savings = currentAnnualPremium - quoteAnnualPremium;
  if (savings <= minSavings) return 0;
  return savings;
}

/**
 * Calculate savings between a collection of quotes and their matching current policies.
 * Exclusive of fees.
 *
 * @param {object[]} quotes An array of bundled quotes
 * @param {object[]} policies An array of a user's current policies
 * @param {object} config
 * @param {object} config.allowMissingPolicy
 * @returns {number} Paid in full amount for the set of quotes
 */
function calcBundledSavings(quotes, policies, { allowMissingPolicy } = { allowMissingPolicy: false }) {
  let savings = 0;
  let missingPolicy = false;
  quotes?.filter(Boolean).forEach(quote => {
    const policy = policies?.find(p => p[QUOTE_FIELDS.COVERAGE_TYPE] === quote[QUOTE_FIELDS.COVERAGE_TYPE]);
    if (!policy) {
      if (allowMissingPolicy) return;
      missingPolicy = true;
      return;
    }
    savings += calcSavings(quote, policy, -Infinity);
  });
  return missingPolicy || savings < 10 ? 0 : savings;
}

/**
 * Finds the cheapest quote in an array according to the quote's lowest annualized premium
 *
 * @param {object[]} quotes
 * @returns {object}
 */
function getCheapestQuote(quotes) {
  const withoutTelematics = maybeOmitTelematics(quotes);
  const { bestQuote } = withoutTelematics.reduce(
    ({ bestQuote, bestPremium }, quote) => {
      const paidInFull = getPaidInFullPremium(quote?.[QUOTE_FIELDS.PREMIUMS]);
      const premium = paidInFull && annualizePremium(paidInFull);

      return premium && premium < bestPremium
        ? {
            bestQuote: quote,
            bestPremium: premium
          }
        : { bestQuote, bestPremium };
    },
    {
      bestQuote: null,
      bestPremium: Infinity
    }
  );
  return bestQuote;
}

/**
 * Returns the quotes without a telematics discount, or the original array if none exist
 *
 * @param {object[]} quotes
 * @returns {object[]}
 */
function maybeOmitTelematics(quotes) {
  const noTelematics = q =>
    !q?.[QUOTE_FIELDS.DISCOUNTS]?.find(d => d?.[DISCOUNT_FIELDS.TYPE] === DISCOUNT_TYPES.TELEMATICS);
  const withoutTelematics = quotes?.filter(noTelematics) || [];
  return withoutTelematics.length ? withoutTelematics : quotes;
}

module.exports = {
  stripNonDigits,
  formatInt,
  stripNonFloatChars,
  formatDecimal,
  formatMoney,
  formatMoneyWithDecimals,
  getBundledPrice,
  getPaidInFullPremium,
  getCheapestPaymentPlan,
  annualizePremium,
  getTotalForInstallmentPlan,
  calcSavings,
  calcBundledSavings,
  getCheapestQuote
};
